import { useEffect, useRef, useState, useCallback } from "react";

/* Brand logo asset — reused verbatim from Hero / OperationsLayer */
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ff176637-490c-4d8d-8821-0039665e7b20/artifacts/1ppcjvci_Web%20logo.png";

/* =============================================================================
 *  Kreux Matrix — Page 3 · Holographic Command Center
 *
 *  Embedded dictionary used VERBATIM per directive. Every text node is bound
 *  to `currentDict`. Brand palette via scoped CSS variables.
 * ===========================================================================*/

const dict = {
  en: {
    Sec1_Title: "The Integration.<br/>Zero Friction. Absolute Power.",
    Sec1_Sub: "You do not need to rebuild your business to enter the future. The Kreux Matrix is not a software replacement; it is a unified intelligence layer engineered to wrap seamlessly around your existing enterprise.",
    Phase1_Title: "Phase I: The Architectural Scan",
    Phase1_Body: "We do not disrupt your momentum. Before a single AI model is deployed, our engineering team maps your existing workflows, daily bottlenecks, and enterprise culture. We learn your business DNA so the AI behaves exactly like your best human talent.",
    Phase2_Title: "Phase II: Seamless Injection",
    Phase2_Body: "Effortless integration. The Kreux Matrix plugs directly into your current phone lines, CRMs, and databases. Whether you run on SAP, Salesforce, or custom internal software, our intelligence injects itself flawlessly behind the scenes. Zero downtime. Zero learning curve for your staff.",
    Phase3_Title: "Phase III: Day Zero Awakening",
    Phase3_Body: "The moment we initiate the system, manual friction vanishes. Your elite digital ecosystem takes over the complex data routing and heavy conversational lifting. Your enterprise runs flawlessly on autopilot, allowing your human leadership to focus entirely on expansion.",
    CTA_Btn: "Initiate The Matrix"
  },
  de: {
    Sec1_Title: "Die Integration.<br/>Null Reibung. Absolute Souveränität.",
    Sec1_Sub: "Sie müssen Ihr Unternehmen nicht umstrukturieren, um in die Zukunft zu treten. Die Kreux-Matrix ist kein Software-Ersatz; sie ist eine integrierte Intelligenzschicht, die sich nahtlos um Ihre bestehende Infrastruktur legt.",
    Phase1_Title: "Phase I: Der Architektonische Scan",
    Phase1_Body: "Wir unterbrechen Ihre Arbeitsabläufe nicht. Bevor ein einziges KI-Modell implementiert wird, analysiert unser Engineering-Team Ihre bestehenden Prozesse, täglichen Engpässe und Unternehmenskultur. Wir entschlüsseln Ihre Business-DNA, damit die KI exakt wie Ihre besten Mitarbeiter agiert.",
    Phase2_Title: "Phase II: Die Nahtlose Injektion",
    Phase2_Body: "Mühelose Integration. Die Kreux-Matrix dockt direkt an Ihre bestehenden Telefonsysteme, CRMs und Datenbanken an. Ob SAP, Salesforce oder interne Individualsoftware – unsere Intelligenz integriert sich fehlerfrei im Hintergrund. Null Ausfallzeit. Null Einarbeitungszeit für Ihr Personal.",
    Phase3_Title: "Phase III: Systemerwachen an Tag Null",
    Phase3_Body: "In dem Moment, in dem wir das System aktivieren, verschwindet jegliche manuelle Reibung. Ihr elitäres digitales Ökosystem übernimmt das komplexe Datenrouting und die anspruchsvolle Kommunikation. Ihr Unternehmen läuft fehlerfrei auf Autopilot, sodass sich Ihre Führungskräfte voll und ganz auf die Expansion konzentrieren können.",
    CTA_Btn: "Die Matrix Initiieren"
  }
};

const CYCLE_MS = 5000;

/* Render the title — split on <br/> for the line break; highlight the
 * final clause starting at "Absolute" in cyan (both EN/DE share that anchor). */
const renderTitle = (raw) => {
  const [line1, line2 = ""] = raw.split("<br/>");
  const absIdx = line2.indexOf("Absolute");
  const head = absIdx >= 0 ? line2.slice(0, absIdx) : line2;
  const tail = absIdx >= 0 ? line2.slice(absIdx) : "";
  return (
    <>
      <span className="block">{line1}</span>
      <span className="block mt-2">
        {head}
        {tail && (
          <span
            className="il-glow italic"
            style={{
              color: "var(--il-cyan)",
              fontFamily: "'Times New Roman', 'EB Garamond', Georgia, serif",
              fontWeight: 400,
            }}
          >
            {tail}
          </span>
        )}
      </span>
    </>
  );
};

const IntegrationLayer = ({ language = "en" }) => {
  const currentDict = dict[language] || dict.en;
  const [activePhase, setActivePhase] = useState(1);
  const [paused, setPaused] = useState(false);
  const cycleTokenRef = useRef(0);

  /* Auto-cycle every 5s — paused on hover */
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActivePhase((p) => (p % 3) + 1);
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [paused]);

  useEffect(() => { cycleTokenRef.current += 1; }, [activePhase]);

  /* CTA — direct DOM exactly per directive */
  const onCta = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      document.getElementById("hero-cta-button")?.click();
    }, 750);
  }, []);

  const phases = [
    { id: 1, title: currentDict.Phase1_Title, body: currentDict.Phase1_Body },
    { id: 2, title: currentDict.Phase2_Title, body: currentDict.Phase2_Body },
    { id: 3, title: currentDict.Phase3_Title, body: currentDict.Phase3_Body },
  ];

  return (
    <section
      id="integration-layer"
      data-testid="integration-layer"
      className="il-root w-full min-h-screen relative overflow-hidden bg-gradient-to-b from-[#112332] to-[#0A1520] font-sans flex flex-col items-center justify-center px-4 sm:px-8 py-24 sm:py-32"
      style={{
        "--il-white": "#FFFFFF",
        "--il-cyan":  "#00E5FF",
        "--il-steel": "#1B3245",
        "--il-navy":  "#112332",
        "--il-amber": "#FFB300",
        color: "var(--il-white)",
      }}
    >
      <style>{styles}</style>

      {/* ── Header ── */}
      <header className="w-full max-w-5xl text-center mb-12 sm:mb-16">
        <h2
          className="font-sans font-extrabold tracking-tighter leading-[1.03] text-5xl sm:text-6xl lg:text-7xl"
          data-testid="il-header-title"
        >
          {renderTitle(currentDict.Sec1_Title)}
        </h2>
        <p
          className="mt-8 mx-auto max-w-3xl font-light text-base sm:text-lg leading-relaxed"
          style={{ color: "rgba(255,255,255,0.55)" }}
        >
          {currentDict.Sec1_Sub}
        </p>
      </header>

      {/* ── The Glass Command Card ── */}
      <div
        className="w-[95%] max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.65)]"
        data-testid="il-dashboard"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{
          border: "1px solid var(--il-steel)",
          background: "rgba(17, 35, 50, 0.40)",
          backdropFilter: "blur(28px) saturate(110%)",
          WebkitBackdropFilter: "blur(28px) saturate(110%)",
        }}
      >
        {/* LEFT — Phase Navigator (left-anchored) */}
        <div className="p-8 lg:p-16 flex flex-col items-start text-left gap-8">
          {phases.map((p) => {
            const isActive = activePhase === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setActivePhase(p.id)}
                data-testid={`il-tab-${p.id}`}
                data-active={isActive}
                className="il-tab w-full text-left relative pl-6 pr-2 py-4 rounded-r-md transition-all duration-700 ease-out group"
                style={{
                  opacity: isActive ? 1 : 0.6,
                  color: isActive ? "var(--il-white)" : "var(--il-steel)",
                  borderLeft: isActive
                    ? "2px solid var(--il-cyan)"
                    : "2px solid transparent",
                  background: isActive
                    ? "linear-gradient(90deg, rgba(27,50,69,0.5), transparent)"
                    : "transparent",
                }}
              >
                <div
                  key={isActive ? `act-${cycleTokenRef.current}` : `idle-${p.id}`}
                  className={isActive ? "il-tabContent--enter" : ""}
                >
                  <span
                    className="block font-mono uppercase tracking-[0.3em] text-[0.6rem] mb-2"
                    style={{ color: isActive ? "var(--il-cyan)" : "rgba(27,50,69,0.9)" }}
                  >
                    PHASE · 0{p.id}
                  </span>
                  <h3
                    className="text-2xl font-bold tracking-tight leading-tight transition-colors duration-700 ease-out"
                    style={{ color: isActive ? "var(--il-white)" : "var(--il-steel)" }}
                  >
                    {p.title}
                  </h3>
                  <div
                    className={
                      "overflow-hidden transition-all duration-700 ease-out " +
                      (isActive ? "max-h-96 mt-4 opacity-100" : "max-h-0 mt-0 opacity-0")
                    }
                  >
                    <p
                      className="font-light leading-relaxed text-sm sm:text-base"
                      style={{ color: "rgba(255,255,255,0.62)" }}
                    >
                      {p.body}
                    </p>
                  </div>
                </div>

                {isActive && (
                  <span
                    key={`bar-${cycleTokenRef.current}`}
                    className="il-progress"
                    style={{
                      animationDuration: paused ? "0s" : `${CYCLE_MS}ms`,
                      animationPlayState: paused ? "paused" : "running",
                      background: "var(--il-cyan)",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* RIGHT — Premium Glassmorphic Logo Vault */}
        <div
          className="w-full h-full min-h-[500px] relative flex items-center justify-center bg-[#112332]/30 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-[inset_0_0_80px_rgba(0,0,0,0.5)] overflow-hidden"
          data-testid="il-visual"
        >
          {/* Cyan radial halo backdrop */}
          <span
            aria-hidden="true"
            className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,229,255,0.15)_0%,transparent_60%)] transition-opacity duration-700 ease-out"
            style={{ opacity: activePhase === 3 ? 0.4 : 1 }}
          />
          {/* Phase II amber wash */}
          <span
            aria-hidden="true"
            className="absolute inset-0 transition-opacity duration-700 ease-out pointer-events-none"
            style={{
              opacity: activePhase === 2 ? 1 : 0,
              background:
                "radial-gradient(circle at center, rgba(255,179,0,0.22) 0%, transparent 55%)",
              filter: "blur(24px)",
            }}
          />
          {/* Phase III steel burst */}
          <span
            aria-hidden="true"
            className="absolute inset-0 transition-opacity duration-700 ease-out pointer-events-none"
            style={{
              opacity: activePhase === 3 ? 1 : 0,
              background:
                "radial-gradient(circle at center, rgba(27,50,69,0.75) 0%, rgba(27,50,69,0.25) 35%, transparent 65%)",
              filter: "blur(10px)",
            }}
          />

          {/* Subtle slow-spinning glass ring frame for depth */}
          <span
            aria-hidden="true"
            className="il-ring il-ring--vault absolute rounded-full border border-white/10 shadow-[0_0_40px_rgba(0,229,255,0.10)]"
          />

          {/* Logo core — pulses + transforms per active phase */}
          <img
            src={LOGO_URL}
            alt="Kreux Matrix"
            data-testid="il-logo"
            className="w-48 z-10 drop-shadow-[0_0_25px_rgba(0,229,255,0.6)] transition-transform duration-700 hover:scale-105"
            style={{
              transform:
                activePhase === 3
                  ? "rotateY(180deg) scale(1.04)"
                  : activePhase === 2
                  ? "scale(0.96)"
                  : "scale(1)",
              filter:
                activePhase === 2
                  ? "drop-shadow(0 0 28px rgba(255,179,0,0.6))"
                  : "drop-shadow(0 0 25px rgba(0,229,255,0.6))",
            }}
          />

          {/* Decorative corner brackets */}
          {[
            { c: "tl", style: { top: 24, left: 24, borderRight: 0, borderBottom: 0 } },
            { c: "tr", style: { top: 24, right: 24, borderLeft: 0, borderBottom: 0 } },
            { c: "bl", style: { bottom: 24, left: 24, borderRight: 0, borderTop: 0 } },
            { c: "br", style: { bottom: 24, right: 24, borderLeft: 0, borderTop: 0 } },
          ].map((c) => (
            <span
              key={c.c}
              className="absolute w-6 h-6 transition-colors duration-700 ease-out z-0"
              style={{
                ...c.style,
                border: "1px solid var(--il-cyan)",
                opacity: 0.35,
              }}
              aria-hidden="true"
            />
          ))}
        </div>
      </div>

      {/* ── Footer CTA — exact 1:1 clone of Page 2 button ── */}
      <div className="w-[95%] max-w-[1500px] mx-auto mt-12 flex justify-center">
        <button
          type="button"
          onClick={onCta}
          data-testid="il-cta-initiate"
          className="bg-[#00E5FF] text-[#112332] font-bold text-sm md:text-base px-8 py-4 flex items-center justify-center gap-3 shadow-[0_0_30px_rgba(0,229,255,0.4)] hover:shadow-[0_0_50px_rgba(0,229,255,0.7)] hover:bg-[#FFFFFF] transition-all duration-300 w-fit rounded-full"
        >
          <span>{currentDict.CTA_Btn}</span>
          <span aria-hidden="true" className="inline-flex">
            <svg viewBox="0 0 16 16" width="14" height="14">
              <path d="M5 3 L11 8 L5 13" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
      </div>
    </section>
  );
};

const styles = `
.il-root .il-glow {
  text-shadow:
    0 0 18px rgba(0, 229, 255, 0.55),
    0 0 38px rgba(0, 229, 255, 0.35);
}

.il-root .il-tabContent--enter {
  animation: ilFadeUp 0.5s ease-out both;
}
@keyframes ilFadeUp {
  0%   { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0);    }
}

.il-root .il-progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 0;
  animation-name: ilProgress;
  animation-timing-function: linear;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
  box-shadow: 0 0 12px rgba(0, 229, 255, 0.7);
}
@keyframes ilProgress {
  0%   { width: 0%;   }
  100% { width: 100%; }
}

.il-root .il-laser {
  animation: ilLaserSweep 2s cubic-bezier(0.36, 0, 0.32, 1) infinite;
}
@keyframes ilLaserSweep {
  0%   { top: 0%;   opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { top: 100%; opacity: 0; }
}

/* Single subtle slow-rotating ring inside the glass vault for depth */
.il-root .il-ring { will-change: transform; pointer-events: none; }
.il-root .il-ring--vault {
  width: 380px; height: 380px;
  animation: ilRingSpin 42s linear infinite;
}
@keyframes ilRingSpin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@media (max-width: 640px) {
  .il-root .il-ring--vault { width: 280px; height: 280px; }
}

.il-root .il-node--pulse {
  animation: ilNodePulse 1.6s cubic-bezier(0.36, 0, 0.32, 1) infinite;
}
@keyframes ilNodePulse {
  0%, 100% { box-shadow: 0 0 12px rgba(0,229,255,0.6); transform: scale(1);    }
  50%      { box-shadow: 0 0 24px rgba(0,229,255,0.95); transform: scale(1.3); }
}

.il-root .il-cta:hover {
  background: var(--il-white) !important;
  color: var(--il-navy) !important;
  box-shadow:
    0 0 0 8px rgba(0, 229, 255, 0.18),
    0 0 48px rgba(0, 229, 255, 0.55);
  transform: translateY(-1px);
}
.il-root .il-cta:active { transform: translateY(0); }

@media (prefers-reduced-motion: reduce) {
  .il-root *,
  .il-root *::before,
  .il-root *::after {
    animation: none !important;
    transition: none !important;
  }
}
`;

export default IntegrationLayer;
